import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import OtpVerificationStep from '../../../components/auth/OtpVerificationStep';
import { isValidEmailFormat } from '../../../components/auth/PersonalDataFields';
import { userErrorMessage } from '../../../lib/userErrors';
import { paidSignupApi } from '../paidSignupApi';
import { useOtpFlow } from '../hooks/useOtpFlow';
import { InlineError, LogoutLink, SignupShell } from '../components/SignupShell';
import { linkButtonClass, primaryButtonClass } from '../constants';

/** Last step: verify the account email, or switch to a corrected one. */
export const VerifyStep: React.FC = () => {
    const { user, sendOTP, verifyOTP, startSession } = useAuth();
    const [changingEmail, setChangingEmail] = useState(false);
    const [newEmailInput, setNewEmailInput] = useState('');
    const [pendingNewEmail, setPendingNewEmail] = useState<string | null>(null);

    const currentEmail = user?.email ?? '';
    const targetEmail = pendingNewEmail ?? currentEmail;

    const otp = useOtpFlow({
        send: async () => {
            if (pendingNewEmail) {
                try {
                    await paidSignupApi.requestEmailChange(pendingNewEmail);
                    return null;
                } catch (err) {
                    return userErrorMessage(err, 'No pudimos reenviar el código');
                }
            }
            const result = await sendOTP(currentEmail);
            return result.success ? null : (result.error || 'No pudimos enviar el código. Usa "Reenviar".');
        },
        verify: async code => {
            if (pendingNewEmail) {
                try {
                    const session = await paidSignupApi.confirmEmailChange(pendingNewEmail, code);
                    startSession(session.access_token, session.user);
                    return null;
                } catch (err) {
                    return userErrorMessage(err, 'Código incorrecto');
                }
            }
            const result = await verifyOTP(currentEmail, code);
            return result.success ? null : (result.error || 'Código incorrecto');
        },
        onVerified: () => {
            toast.success('¡Bienvenido a Reveca!', { description: 'Tu correo quedó verificado.' });
        },
    });

    const handleRequestChange = async (e: React.FormEvent) => {
        e.preventDefault();
        const email = newEmailInput.trim().toLowerCase();
        if (!isValidEmailFormat(email)) {
            otp.setError('Escribe un correo válido');
            return;
        }
        otp.setLoading(true);
        otp.setError(null);
        try {
            await paidSignupApi.requestEmailChange(email);
            setPendingNewEmail(email);
            setChangingEmail(false);
            otp.setCode('');
            toast.success('Te enviamos un código al nuevo correo');
        } catch (err: any) {
            otp.setError(userErrorMessage(err, 'No pudimos enviar el código a ese correo'));
        } finally {
            otp.setLoading(false);
        }
    };

    return (
        <SignupShell title="Verifica tu correo" subtitle="Último paso para empezar a usar Reveca">
            <div className="space-y-4">
                <InlineError message={otp.error} />

                {changingEmail ? (
                    <form onSubmit={handleRequestChange} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Nuevo correo *</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    value={newEmailInput}
                                    onChange={e => setNewEmailInput(e.target.value)}
                                    placeholder="tu@email.com"
                                    autoComplete="email"
                                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#1CB3B3] focus:bg-[#E8F7F7] transition-all duration-200"
                                    disabled={otp.loading}
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                Te enviaremos un código a este correo y quedará como el correo de tu cuenta.
                            </p>
                        </div>
                        <button type="submit" disabled={otp.loading || !newEmailInput} className={primaryButtonClass}>
                            {otp.loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar código'}
                        </button>
                        <p className="text-center text-sm">
                            <button
                                type="button"
                                onClick={() => setChangingEmail(false)}
                                disabled={otp.loading}
                                className={linkButtonClass}
                            >
                                Volver
                            </button>
                        </p>
                    </form>
                ) : (
                    <OtpVerificationStep
                        email={targetEmail}
                        code={otp.code}
                        onCodeChange={otp.setCode}
                        onSubmit={otp.submit}
                        onResend={otp.resend}
                        onChangeEmail={() => {
                            otp.setError(null);
                            setNewEmailInput('');
                            setChangingEmail(true);
                        }}
                        loading={otp.loading}
                    />
                )}
            </div>
            <LogoutLink disabled={otp.loading} />
        </SignupShell>
    );
};
